// KONTAKTKORTET — det viktigste skjermbildet i NJORD.
//
// ══ EIERKRAVET, ORDRETT ═════════════════════════════════════════════════════
// «Det er viktig at man også skal kunne klikke på "targets" og få alle sensorene
//  som observerer denne "faren" og få laget målløsninger i Skade.»
//
// ══ HVA KORTET LOVER, OG HVORDAN LØFTET HOLDES ══════════════════════════════
// Kortet skal svare på fire spørsmål uten at operatøren tolker noe:
//
//   1. HVEM SER DEN?        — én rad per sensor på posten, ALLTID alle radene.
//   2. HVA MÅLTE DE?        — i sensorens egne enheter (peiling / peiling+avstand
//                             / frekvens / lydsignatur / bilde), ikke «deteksjon».
//   3. NÅR, OG HVOR SIKKERT? — klokkeslett + alder + sikkerhetsbånd per rad.
//   4. HVEM SER DEN IKKE?   — eksplisitt, med ÅRSAK, i en egen bolk.
//
// Punkt 4 er det som skiller kortet fra en observasjonsliste. En kontakt sett av
// én sensor er en INDIKASJON; en sett av tre er et FAKTUM. Skal operatøren se
// forskjellen uten å tolke, må de manglende sensorene stå der som rader — ikke
// som noe hun må huske å savne. Og årsakene er ikke likeverdige: en radar som
// måler friskt og likevel ikke ser noe der RF-en peker, er selve signaturen til
// et lite, lavt mål — altså kanskje en USV.
//
// Rekkefølgen i kortet er lest ovenfra og ned som en beslutning: hva er dette →
// hvor sikkert er det → hvem sier hva → hva gjør jeg.
import { can } from '@skade/rbac';
// `Dekningsrad` bor i fusjon.ts (den er et resultat av dekningsberegningen, ikke
// en wire-type), resten i typer.ts. Importene peker derfor to steder — det er
// riktig, og en samle-reeksport ville bare skjult hvor ting faktisk defineres.
import {
  FARTOYKLASSE_NAVN, SENSORART_MAALER, SENSORART_NAVN,
  type Kystkontakt,
} from '../api/typer.js';
import type { Dekningsrad } from '../lib/fusjon.js';
import {
  BEKREFTELSE_NAVN, bekreftelse, dekningssammendrag, IKKE_SETT_TEKST, sensordekning,
} from '../lib/fusjon.js';
import { formaterFart, maalingTekst, maalingslagNavn } from '../lib/format.js';
import { GRUNNLAG_TEKST, KVALITET_TEKST, lagMaalloesning } from '../lib/maalloesning.js';
import { trusselband, TRUSSELBAND_NAVN, trusselvurdering } from '../lib/trussel.js';
import { artOppslag } from '../lib/lager.js';
// DOM-byggerne, tomtilstandene, friskhetsteksten og de generelle formatererne er
// flatens felles kjerne (`@skade/sensorflate`); målingsteksten, farten i knop og
// dekningen over er kystspesifikke.
import {
  div, felt, formaterAlder, formaterKlokke, formaterKurs, formaterSikkerhet, h, knapp,
  kopierknapp, kvittering, merke, NIVAA_NAVN, nv, p, sett, sikkerhetsband, span, statusprikk, tomt,
} from '@skade/sensorflate';
import { ikon } from '../ui/ikoner.js';
import type { Kontekst } from './kontekst.js';

/** Ikonet for en sensorart. Navnene i `ikoner.ts` speiler artene med vilje. */
const ART_IKON = { radar: 'radar', rf: 'rf', hydrofon: 'hydrofon', kamera: 'kamera' } as const;

export function tegnKontaktkort(vert: HTMLElement, ctx: Kontekst): void {
  const k = ctx.valgt();
  if (!k) {
    sett(vert, tomt(
      'Ingen kontakt valgt',
      'Velg en kontakt i lista over — eller på kartet — for å se hvilke sensorer som ser den, hva de målte, og hvilke som ikke ser den.',
      // Fartøysymbolet er NJORDs eget ikon, så det sendes inn som ferdig node.
      { ikon: ikon('kontakt') },
    ));
    return;
  }
  const t = ctx.tilstand();
  const naa = ctx.naa();
  const dekning = sensordekning(k, t.sensorer, { naaMs: naa });
  const vurdering = trusselvurdering(k, naa, artOppslag(t), {
    post: t.post?.lat !== undefined && t.post.lon !== undefined ? { lat: t.post.lat, lon: t.post.lon } : undefined,
  });

  const rot = div('kort');
  rot.append(tegnIdentitet(k, vurdering.poeng, ctx.naa()));
  rot.append(tegnBekreftelse(dekning));
  rot.append(tegnGrunnlag(k));
  rot.append(tegnSerDen(dekning, naa));
  rot.append(tegnSerDenIkke(dekning));
  rot.append(tegnHandlinger(k, ctx));
  sett(vert, rot);
}

// ── 1. Hva er dette ─────────────────────────────────────────────────────────

function tegnIdentitet(k: Kystkontakt, poeng: number, naaMs: number): HTMLElement {
  const rot = div('kk-topp');
  const band = trusselband(poeng);
  const linje = div('kk-navnlinje');
  linje.append(h(2, 'kk-navn', k.navn || k.id));
  linje.append(merke(`${TRUSSELBAND_NAVN[band]} ${poeng}`, `band-${band}`));
  linje.append(merke(FARTOYKLASSE_NAVN[k.klasse], `klasse-${k.klasse}`));
  linje.append(merke(TILHORIGHET_NAVN[k.tilhorighet], `aff-${k.tilhorighet}`));
    // Øvingsmerket står PÅ kontakten og ikke bare i toppbanneret: et utsnitt av
  // skjermen skal ikke kunne forveksles med et ekte situasjonsbilde (§1), og et
  // kontaktkort er nettopp det utsnittet folk klipper ut.
  if (k.ovelse) linje.append(merke('ØVING', 'ovelse'));
  rot.append(linje);

  const tall = div('kk-tallrad');
  tall.append(felt('Fart', formaterFart(k.fartKn)));
  tall.append(felt('Kurs', formaterKurs(k.kursDeg)));
    // ctx.naa(), IKKE Date.now(): alle alderstall i kortet skal komme fra SAMME
  // klokke, ellers kan to rader vise ulik alder på samme måling. Dette var det
  // ene stedet i kortet som brøt regelen kontekst.ts er skrevet for å håndheve.
  tall.append(felt('Sist sett', formaterAlder(naaMs - k.sisteTsMs)));
  tall.append(felt('Klokke', formaterKlokke(k.sisteTsMs)));
  rot.append(tall);
  return rot;
}

const TILHORIGHET_NAVN = {
  friend: 'Egen/venn', hostile: 'Hostil', neutral: 'Nøytral', unknown: 'Ukjent tilhørighet',
} as const;

// ── 2. Hvor sikkert er det ──────────────────────────────────────────────────

function tegnBekreftelse(dekning: ReturnType<typeof sensordekning>): HTMLElement {
  const b = bekreftelse(dekning);
  const rot = div(`kk-bekreftelse kk-bekreftelse-${b}`);
  rot.append(h(3, 'kk-bekreftelse-tittel', BEKREFTELSE_NAVN[b]));
  rot.append(p('kk-bekreftelse-brød', dekningssammendrag(dekning)));

  // Tellingen står som TALL og ikke bare som farge, fordi «2 av 4» er svaret på
  // spørsmålet operatøren egentlig stiller: kan jeg handle på dette?
  const teller = div('kk-teller');
  teller.append(felt('Ser den', `${dekning.serDen.length} av ${dekning.rader.length}`, 'ser'));
  teller.append(felt('Sensorarter', String(dekning.arter.length), 'arter'));
  teller.append(felt('Måler, ser ikke', String(dekning.friskeSomIkkeSer), 'ikke'));
  rot.append(teller);

  if (dekning.friskeSomIkkeSer > 0 && dekning.arter.length === 1) {
    // Dette er den operative innsikten kortet finnes for. Den skal stå som en
    // setning, ikke være noe operatøren utleder fra to tall.
    rot.append(p('kk-innsikt',
      `${dekning.friskeSomIkkeSer} sensor(er) måler nå og ser den likevel ikke. `
      + 'Et lite, lavt mål (USV / rib med lav profil) gir nettopp dette bildet — men det gjør en falsk kontakt også. Krysspeil før du handler.'));
  }
  return rot;
}

// ── 3. Grunnlaget: målt eller antatt posisjon ────────────────────────────────

function tegnGrunnlag(k: Kystkontakt): HTMLElement {
  const rot = div('kk-bolk');
  rot.append(bolkhode('Posisjon'));
  if (k.lat === undefined || k.lon === undefined) {
    rot.append(p('kk-brød', 'Ingen posisjon. Kontakten er kjent gjennom peilinger, men fusjonen har ikke løst hvor den er.'));
    return rot;
  }
  rot.append(nv('Bredde', k.lat.toFixed(5)));
  rot.append(nv('Lengde', k.lon.toFixed(5)));
  rot.append(nv('Kvalitet', k.posisjonLost ? 'Løst — krysspeilt eller målt' : 'PROJISERT fra én peiling — avstanden er antatt',
    k.posisjonLost ? 'ok' : 'advarsel'));
  return rot;
}

// ── 4. Hvem ser den, og hva målte de ────────────────────────────────────────

function tegnSerDen(dekning: ReturnType<typeof sensordekning>, naa: number): HTMLElement {
  const rot = div('kk-bolk');
  rot.append(bolkhode(`Sensorer som ser kontakten (${dekning.serDen.length})`));
  if (dekning.serDen.length === 0) {
    rot.append(p('kk-brød', 'Ingen sensor har en observasjon av denne kontakten nå. Det som vises over er siste kjente tilstand.'));
    return rot;
  }
  for (const rad of dekning.serDen) rot.append(tegnSensorrad(rad, naa));
  return rot;
}

/**
 * Én sensorrad. Rekkefølgen inne i raden er svaret på de tre spørsmålene i
 * samme orden hver gang: HVEM (navn + art) · HVA (målingen) · NÅR + HVOR SIKKERT.
 * Fast rekkefølge er hele grunnen til at raden kan leses på et blikk.
 */
function tegnSensorrad(rad: Dekningsrad, naa: number): HTMLElement {
  const o = rad.observasjon!;
  const alder = Math.max(0, naa - o.tsMs);
  const rot = div(`kk-sensor kk-sensor-${rad.status.friskhet}`);

  const hode = div('kk-sensor-hode');
  hode.append(ikon(ART_IKON[rad.sensor.art]));
  hode.append(span('kk-sensor-navn', rad.sensor.navn));
  hode.append(merke(SENSORART_NAVN[rad.sensor.art], `art-${rad.sensor.art}`));
  hode.append(div('kk-rom'));
  hode.append(merke(maalingslagNavn(o.maaling), 'slag'));
  rot.append(hode);

  // HVA sensoren faktisk målte — i sensorens egne enheter. Dette er raden.
  rot.append(p('kk-maaling', maalingTekst(o.maaling)));

  const fot = div('kk-sensor-fot');
  fot.append(felt('Tidspunkt', formaterKlokke(o.tsMs)));
  fot.append(felt('Alder', formaterAlder(alder), `alder-${rad.status.friskhet}`));
  fot.append(felt('Sikkerhet', formaterSikkerhet(o.sikkerhet), `sikker-${sikkerhetsband(o.sikkerhet)}`));
  if (rad.historikk.length > 1) fot.append(felt('Observasjoner', String(rad.historikk.length)));
  rot.append(fot);

  // Sensoren kan være degradert selv når målingen er fersk. Da må BÅDE målingen
  // og forbeholdet stå — ikke det ene eller det andre.
  if (rad.status.nivaa !== 'ok') {
    const adv = div('kk-sensoradvarsel');
    adv.append(statusprikk(NIVAA_NAVN[rad.status.nivaa], `niv-${rad.status.nivaa}`));
    adv.append(span('kk-sensoradvarsel-tekst', rad.status.grunner.join(' · ')));
    rot.append(adv);
  }
  return rot;
}

// ── 5. Hvem ser den IKKE — kortets andre halvdel ─────────────────────────────

/**
 * Denne bolken er ikke en «tomtilstand». Den er DATA: hver sensor som ikke ser
 * kontakten står med sin egen årsak, og årsakene sorteres slik at den mest
 * informative står øverst — «måler, men ser ikke» sier noe om kontakten, mens
 * «kan ikke se» bare sier noe om sensoren.
 */
function tegnSerDenIkke(dekning: ReturnType<typeof sensordekning>): HTMLElement {
  const rot = div('kk-bolk kk-bolk-negativ');
  rot.append(bolkhode(`Sensorer som IKKE ser kontakten (${dekning.serDenIkke.length})`));
  if (dekning.serDenIkke.length === 0) {
    rot.append(p('kk-brød', 'Alle sensorene på posten ser kontakten. Så godt bekreftet blir det ikke.'));
    return rot;
  }
  const rang = { 'ser-ikke': 0, 'utenfor-rekkevidde': 1, 'kan-ikke': 2 } as const;
  const sortert = [...dekning.serDenIkke].sort((a, b) =>
    rang[a.ikkeSett ?? 'kan-ikke'] - rang[b.ikkeSett ?? 'kan-ikke']);
  for (const rad of sortert) {
    const grunn = rad.ikkeSett ?? 'kan-ikke';
    const linje = div(`kk-negativ kk-negativ-${grunn}`);
    const hode = div('kk-sensor-hode');
    hode.append(ikon(ART_IKON[rad.sensor.art]));
    hode.append(span('kk-sensor-navn', rad.sensor.navn));
    hode.append(div('kk-rom'));
    hode.append(statusprikk(NIVAA_NAVN[rad.status.nivaa], `niv-${rad.status.nivaa}`));
    linje.append(hode);
    linje.append(p('kk-negativ-grunn', IKKE_SETT_TEKST[grunn]));
    // Hva sensoren VILLE målt om den så noe. Det gjør den negative raden
    // handlingsbar: operatøren vet hva som mangler i bildet.
    linje.append(p('kk-negativ-hva', `Ville gitt: ${SENSORART_MAALER[rad.sensor.art]}`));
    if (rad.status.grunner.length > 0) {
      linje.append(p('kk-negativ-detalj', rad.status.grunner.join(' · ')));
    }
    linje.append(p('kk-negativ-detalj', `Historikk på denne kontakten: ${rad.historikk.length} observasjon(er)`));
    rot.append(linje);
  }
  return rot;
}

// ── 6. Hva gjør jeg: målløsningen ───────────────────────────────────────────

/**
 * «Lag målløsning i SKADE».
 *
 * Knappen gjør to ting som er STRENGT ATSKILT, og det skillet er selve
 * ærligheten her:
 *   A. REGNER UT løsningen lokalt, med geometrien fra `@skade/contracts`. Det
 *      virker uten bro og uten innlogging, og gir operatøren et grid hun kan
 *      lese opp over sambandet med en gang. Ekte verdi, ikke en attrapp.
 *   B. SENDER den til SKADE. Det krever både rettighet (RBAC) og en tilkoblet
 *      bro. Mangler noe av det, står det HVA som mangler — knappen later aldri
 *      som den sendte noe.
 *
 * Klient-sjekken av rettigheten er kosmetisk; hubben håndhever autoritativt
 * (grunnloven §4). Den er her for at operatøren skal vite hvorfor, ikke som en
 * sikkerhetsmekanisme.
 */
function tegnHandlinger(k: Kystkontakt, ctx: Kontekst): HTMLElement {
  const rot = div('kk-bolk kk-handlinger');
  rot.append(bolkhode('Målløsning'));
  const t = ctx.tilstand();
  const forsøk = lagMaalloesning(k, t.sensorer);
  const resultat = div('kk-loesning');

  if (!forsøk.ok) {
    resultat.append(p('kk-avslag-grunn', forsøk.avslag.grunn));
    resultat.append(p('kk-avslag-raad', forsøk.avslag.raad));
    rot.append(resultat);
    rot.append(knapp('Lag målløsning i SKADE', undefined, {
      variant: 'primær', ikon: ikon('maal'), deaktivert: true,
      tittel: forsøk.avslag.grunn,
    }));
    return rot;
  }

  const l = forsøk.loesning;
  resultat.append(nv('Grid (MGRS)', l.mgrs, 'grid'));
  resultat.append(nv('Grunnlag', GRUNNLAG_TEKST[l.grunnlag]));
  resultat.append(nv('Kvalitet', KVALITET_TEKST[l.kvalitet], l.kvalitet === 'skarp' ? 'ok' : 'advarsel'));
  resultat.append(nv('Feilradius', l.cepM === undefined ? 'Ikke oppgitt av broen' : `≈ ${l.cepM} m`,
    l.cepM === undefined ? 'advarsel' : ''));
  resultat.append(p('kk-begrunnelse', l.begrunnelse));
  rot.append(resultat);

  const rad = div('kk-knapperad');
  // Kopiering virker alltid og krever ingen bro. På en post der sambandet til
  // hubben kan være nede, men VHF-en virker, er dette den ekte veien videre.
  rad.append(kopierknapp(`${l.mgrs} — ${l.begrunnelse}`, 'Målløsningen', (m, v) => {
    kvittering(m, v);
    ctx.kunngjør(m);
  }));

  const okt = ctx.okt();
  const org = t.post?.distrikt ? { district: t.post.distrikt, area: t.post.omrade } : undefined;
  const harRett = okt.bruker !== null && can(okt.bruker, 'write', { resource: 'target_list', org });
  const tilkoblet = ctx.tilkobling().tilstand === 'tilkoblet';
  const sperre = !okt.bruker
    ? 'NJORD har ingen innlogging ennå, så rollen din er ukjent. Målløsningen kan ikke sendes til SKADE herfra — kopiér den og meld den inn.'
    : !harRett
      ? 'Rollen din har ikke skriverett på mållister. Send løsningen via en som har (områdesjef eller høyere).'
      : !tilkoblet
        ? 'Ingen KOP-bro tilkoblet — løsningen kan ikke sendes nå. Den er regnet ut og kan kopieres.'
        : null;

  rad.append(knapp('Lag målløsning i SKADE', () => {
    const sendt = ctx.send({ type: 'maalloesning', kontaktId: k.id, mgrs: l.mgrs, begrunnelse: l.begrunnelse });
    if (sendt) {
      kvittering(`Målløsning sendt til SKADE: ${l.mgrs}`);
      ctx.kunngjør(`Målløsning sendt til SKADE, grid ${l.mgrs}`);
    } else {
      kvittering('Kunne ikke sende — ingen forbindelse til KOP-broen.', 'feil');
    }
  }, { variant: 'primær', ikon: ikon('maal'), deaktivert: sperre !== null, tittel: sperre ?? undefined }));
  rot.append(rad);

  if (sperre) rot.append(p('kk-sperre', sperre));

  // Operatørens flagg. Det ligger her, under målløsningen, fordi det er den
  // andre tingen man gjør etter å ha lest kortet: si fra hva man mener om
  // kontakten. Flagget overstyrer trusselsorteringen (se `trussel.ts`).
  const flagg = div('kk-flagg');
  flagg.append(span('kk-flagg-etikett', 'Min vurdering:'));
  for (const [verdi, navn] of [['trussel', 'Trussel'], ['folg', 'Følg'], ['ingen', 'Nøytral'], ['avskrevet', 'Avskriv']] as const) {
    const b = knapp(navn, () => {
      ctx.settFlagg(k.id, verdi);
      ctx.kunngjør(`${k.navn || k.id} merket «${navn}»`);
    }, { variant: k.flagg === verdi ? 'primær' : 'stille' });
    b.setAttribute('aria-pressed', k.flagg === verdi ? 'true' : 'false');
    flagg.append(b);
  }
  rot.append(flagg);
  return rot;
}

function bolkhode(tekst: string): HTMLElement {
  return h(3, 'kk-bolkhode', tekst);
}
